import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronDown, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/AppShell";
import { CoordinationBoard } from "@/components/CoordinationBoard";
import { frenchDate } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return toISODate(d);
}
function mondayOf(date) {
  const d = typeof date === "string" ? new Date(date + "T00:00:00") : new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return toISODate(d);
}

export default function SuiviCoordination() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const vperm = user?.permissions || {};
  const canDeclare = role === "admin" || (role === "lecture_seule" && !!vperm.declare_coordination);
  const canOpenSpot = role === "admin" || role === "cdp";

  // Read week parameter from URL, fallback to current week
  const [searchParams] = useSearchParams();
  const weekParam = searchParams.get("week");
  const initialMonday = weekParam && /^\d{4}-\d{2}-\d{2}$/.test(weekParam) 
    ? mondayOf(weekParam) 
    : mondayOf(new Date());
  
  const [monday, setMonday] = useState(initialMonday);
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Update monday when URL parameter changes
  useEffect(() => {
    const wk = searchParams.get("week");
    if (wk && /^\d{4}-\d{2}-\d{2}$/.test(wk)) {
      setMonday(mondayOf(wk));
    }
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/planning/coordination", { params: { week: monday } });
      setData(data);
    } catch (e) {
      toast.error("Chargement du suivi impossible");
    } finally {
      setLoading(false);
    }
  }, [monday]);

  const softLoad = useCallback(async () => {
    try {
      const { data } = await api.get("/planning/coordination", { params: { week: monday } });
      setData(data);
    } catch { /* silencieux */ }
  }, [monday]);

  useEffect(() => { load(); }, [load]);

  const declareBooking = async (item, declared) => {
    try {
      await api.post(`/planning/booking/${item.id}/declare`, { declared });
      toast.success(declared
        ? `« ${item.spot_name} » déclaré à la coordination`
        : `Déclaration retirée pour « ${item.spot_name} »`);
      await softLoad();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
      await softLoad();
    }
  };

  const range = useMemo(() => {
    const dd = data?.days || [];
    const first = dd[0]?.date;
    const last = dd[dd.length - 1]?.date || (first ? addDays(monday, 6) : null);
    return first ? `${frenchDate(first)} – ${frenchDate(last)}` : "";
  }, [data, monday]);

  return (
    <>
      <PageHeader
        title="Suivi coordination"
        subtitle="Créneaux placés déclarés à la coordination, jour par jour, pour la semaine en cours."
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="ghost" size="icon" onClick={() => setMonday(addDays(monday, -7))} data-testid="coord-week-prev" className="h-9 w-9 border border-[var(--border-soft)] bg-white"><ChevronLeft className="h-4 w-4" /></Button>
            <Popover open={weekPickerOpen} onOpenChange={setWeekPickerOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="coord-week-jump-trigger"
                  className="group rounded-lg border border-[var(--border-soft)] bg-white px-4 py-1.5 text-center transition-colors hover:border-[var(--shariti-orange)] hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]">
                  <div className="text-[10px] font-semibold uppercase text-muted-ink">Semaine {data?.week_number}</div>
                  <div className="flex items-center justify-center gap-1.5 text-sm font-bold" data-testid="coord-week-range">
                    {range}
                    <ChevronDown className="h-3.5 w-3.5 text-muted-ink" />
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="center" className="w-72">
                <div className="space-y-2">
                  <div className="text-xs font-bold text-ink">Aller à une semaine</div>
                  <Input type="date" value={monday}
                    onChange={(e) => { if (e.target.value) { setMonday(mondayOf(e.target.value)); setWeekPickerOpen(false); } }}
                    className="h-9" data-testid="coord-week-jump-date" />
                  <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="coord-week-today"
                    onClick={() => { setMonday(mondayOf(new Date())); setWeekPickerOpen(false); }}>Cette semaine</Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={() => setMonday(addDays(monday, 7))} data-testid="coord-week-next" className="h-9 w-9 border border-[var(--border-soft)] bg-white"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" onClick={() => setMonday(mondayOf(new Date()))} data-testid="coord-week-today-btn" className="h-9 border border-[var(--border-soft)] bg-white text-xs"><CalendarClock className="mr-1 h-4 w-4" /> Aujourd'hui</Button>
          </div>
        }
      />
      <div className="px-4 py-4 sm:px-6 lg:px-8">
        <CoordinationBoard
          weekData={data}
          loading={loading}
          canDeclare={canDeclare}
          onDeclare={declareBooking}
          weekMonday={monday}
          onOpenSpot={canOpenSpot ? (item) => navigate(`/spots/${item.spot_id}`) : undefined}
        />
      </div>
    </>
  );
}
